/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 25/09/2024 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Bloqueo de abono simple al cubrir apartado
*      3       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Apertura estable del modal de cobros
*      4       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Cobro sin peticiones al abrir abonos
*      5       | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Observación opcional para movimientos de crédito
* --------------------------------------------------------------------------------------- */

import TextField from "../ui/TextField";
import React, { Fragment, useState, useEffect, useRef, useCallback } from "react";
import { NumericFormat } from 'react-number-format';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { AttachMoney, CreditCard, AccountBalance, CallToAction, AccountBalanceWallet } from '@mui/icons-material'; // Importa los íconos que necesites

import { Modal, Box, MenuItem, Typography } from "@mui/material";
import { NumberFormat$ } from "../../settings/utils";
import { useVentas } from '../../context/Ventas/VentasContext'
import { useDoubleClickPrevention } from '../../hooks/useDoubleClickPrevention';

// Estilos para el modal con efecto suavizado de desplazamiento
const modalStyle = {
    position: 'fixed',
    top: '6%',
    left: '50%',
    width: '49vw',
    maxWidth: '82vw',
    maxHeight: '100vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    display: 'flex',
    flexDirection: 'column',
    transform: 'translateX(-50%)',
    overflowY: 'auto',
    overflowX: 'auto'
};

const getTotales = () => ({
    subTotal: 0,
    totalDescuento: 0,
    totalIVA: 0,
    total: 0,
    tipoPago: "EFECTIVO",
    montoTipoPago: 0,
    montoEfectivo: 0,
    montoDescuento: 0,
    totalCambioDevueltoACliente: 0,
    totalEnLetra: "",
    serialTarjetaDebitoCreditoChequeVale: "",
});

function NumericFormatCustom(props) {
    const { inputRef, onChange, ...other } = props;

    return (
        <NumericFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value, // Devuelve solo el valor numérico sin formato.
                    },
                });
            }}
            thousandSeparator // Añade separador de miles
            decimalSeparator="." // Define el separador decimal como un punto
            prefix="$" // Prefijo de moneda, opcional
            decimalScale={2} // Número de decimales
        />
    );
}

export default function ModalCobrosGenerales(props) {
    const {
        openModalCobrarVentas,
        montosVenta,
        setMontosVenta,
        handleCloseModalCobroVentas,
        handleCobrar,
        modoEntregaDefinitiva = false,
        bloquearCobroTotalSimple = false,
        calcularCambioEnLetra = true,
        observacion = "",
        onChangeObservacion,
    } = props;

    const { MontoEnLetra, parametrosCajaForm } = useVentas();

    const { isProcessing, executeWithProtection } = useDoubleClickPrevention();

    const [montVenta, setMontVenta] = useState(getTotales());

    const [montoCambioLetra, setMontoCambioLetra] = useState("CERO PESOS");
    const [montoCambio, setMontoCambio] = useState(0);
    const debounceTimerRef = useRef(null);
    const refMontoEfectivo = useRef(null);
    const refMontoOtro = useRef(null);
    const refDigitos = useRef(null);
    const refTransferencia = useRef(null);
    const refCheque = useRef(null);
    const refVales = useRef(null);

    const focusInputByPaymentType = useCallback((tipoPago) => {
        if (tipoPago === "EFECTIVO") {
            refMontoEfectivo.current?.focus?.();
            refMontoEfectivo.current?.select?.();
        } else if (tipoPago === "TARJETA DÉBITO" || tipoPago === "TARJETA CRÉDITO") {
            refDigitos.current?.focus?.();
        } else if (tipoPago === "TRANSFERENCIA") {
            refTransferencia.current?.focus?.();
        } else if (tipoPago === "CHEQUE") {
            refCheque.current?.focus?.();
        } else if (tipoPago === "VALES") {
            refVales.current?.focus?.();
        } else {
            refMontoOtro.current?.focus?.();
        }
    }, []);

    const CobrarVenta = () => {
        if (bloqueaCobroPorEntregaDefinitiva) {
            return;
        }

        executeWithProtection(async () => {
            const data = {
                foliocortecaja: "",
                remision: "",
                pc: "",
                usuario: "",
                clavecliente: "",
                razonsocial: "",
                fechaventa: "",
                horaventa: "",
                factura: 0,
                facturaelectronica: "",
                condicionesdepago: "",
                tipopago: (montVenta.tipoPago || "EFECTIVO").trim(),
                serialtarjetachequevale: (datosTransferencia || "").trim() + (datosCheque || "").trim() + (datosVale || "").trim() + (digitosTarjeta || "").trim(),
                totaltipopago: montoTipoPago,
                totalefectivo: montoEfectivo,
                totaldescuento: montVenta.totalDescuento,
                totalcambio: montoCambio,
                numeroautorizacion: digitosTarjeta,
                tipodebitocredito: (montVenta.tipoPago || "") === "TARJETA CRÉDITO" ? "CRÉDITO" : (montVenta.tipoPago || "") === "TARJETA DÉBITO" ? "DÉBITO" : "",
                banco: "",
                subtotal: montVenta.subTotal,
                iva: montVenta.totalIVA,
                total: montVenta.total,
                totalenletra: montVenta.montoEnLetra
            }

            try {
                await handleCobrar(data);
            } finally {
                handleCloseModalCobroVentas();
                InicializarCuenta();
            }
        });
    };

    const InicializarCuenta = () => {
        changeStatusCobro(false);
        setDatosTransferencia("");
        setDigitosTarjeta("");
        setDatosCheque("");
        setDatosVale("");
        setMontoEfectivo(0);
        setMontoTipoPago(0);
        setMontVenta(getTotales());
        CalcularCambio();
    };

    const [montoTipoPago, setMontoTipoPago] = useState(0);
    const changeTipoPago = (e) => {
        setMontoTipoPago(Number(e.target.value) || 0);
    };

    const [digitosTarjeta, setDigitosTarjeta] = useState("0000");
    const changeDigitosTarjeta = (e) => {
        setDigitosTarjeta(e.target.value);
    };

    const [datosCheque, setDatosCheque] = useState("");
    const changeDatosCheque = (e) => {
        setDatosCheque(e.target.value);
    };

    const [montoEfectivo, setMontoEfectivo] = useState(0);
    const changeEfectivo = (e) => {
        setMontoEfectivo(Number(e.target.value) || 0);
    };

    const [datosVale, setDatosVale] = useState("");
    const changeDatosVale = (e) => {
        setDatosVale(e.target.value);
    };

    const [statusCobro, setStatusCobro] = useState(false);
    const statusCobroRef = useRef(statusCobro);
    const totalPagado = (Number(montoTipoPago) || 0) + (Number(montoEfectivo) || 0);
    const totalApartado = Number(montVenta.total) || 0;
    const bloqueaCobroPorEntregaDefinitiva =
        bloquearCobroTotalSimple &&
        !modoEntregaDefinitiva &&
        totalApartado > 0 &&
        totalPagado >= totalApartado;
    const changeStatusCobro = (status) => {
        setStatusCobro(status);
    };

    const handleChangeTipoPago = (tipox) => {
        setMontosVenta((dataPrev) => ({
            ...dataPrev,
            tipoPago: tipox,
        }));

        setMontoEfectivo(0);
        setMontoTipoPago(0);
        setDigitosTarjeta("");
        setDatosTransferencia("");
        setDatosCheque("");
        setDatosVale("");
        setStatusCobro(false);
        setMontoCambio(0);
        setMontoCambioLetra("CERO PESOS");
        
        setTimeout(() => {
            focusInputByPaymentType(tipox);
        }, 50);
    };

    const [datosTransferencia, setDatosTransferencia] = useState("");
    const changeDatosTransferencia = (e) => {
        setDatosTransferencia(e.target.value);
    };

    useEffect(() => {
        statusCobroRef.current = statusCobro && !bloqueaCobroPorEntregaDefinitiva;
    }, [statusCobro, bloqueaCobroPorEntregaDefinitiva]);

const CalcularCambio = () => {
        const tipoPagoNum = Number(montoTipoPago) || 0;
        const efectivoNum = Number(montoEfectivo) || 0;
        const totalVenta = Number(montVenta.total) || 0;

        const Sumas = tipoPagoNum + efectivoNum;
        const Cambio = Math.round((Sumas - totalVenta) * 100) / 100;

        if (efectivoNum <= 0 && tipoPagoNum <= 0) {
            changeStatusCobro(false);
        }
        else {
            changeStatusCobro(true);
        }

        setMontoCambio(Cambio);
    };

    useEffect(() => {
        if (openModalCobrarVentas) {
            setTimeout(() => focusInputByPaymentType(montVenta.tipoPago || "EFECTIVO"), 50);
        }

        if (!openModalCobrarVentas) {
            return;
        }

        const handleKeyDown = (event) => {
            if (event.altKey && (event.key === "End" || event.code === "End")) {
                event.preventDefault();
                if (statusCobroRef.current) CobrarVenta();
                return;
            }
            if (event.key === 'F1') {
                event.preventDefault();
                return;
            }
            if (event.altKey && event.key.toLowerCase() === 'e') {
                event.preventDefault();
                handleChangeTipoPago('EFECTIVO');
                return;
            }
            if (event.altKey && event.key.toLowerCase() === 'c') {
                event.preventDefault();
                handleChangeTipoPago('TARJETA CRÉDITO');
                return;
            }
            if (event.altKey && event.key.toLowerCase() === 'd') {
                event.preventDefault();
                handleChangeTipoPago('TARJETA DÉBITO');
                return;
            }
            if (event.altKey && event.key.toLowerCase() === 'q') {
                event.preventDefault();
                handleChangeTipoPago('CHEQUE');
                return;
            }
            if (event.altKey && event.key.toLowerCase() === 'v') {
                event.preventDefault();
                handleChangeTipoPago('VALES');
                return;
            }
            if (event.altKey && event.key.toLowerCase() === 't') {
                event.preventDefault();
                handleChangeTipoPago('TRANSFERENCIA');
                return;
            }

        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [openModalCobrarVentas]);

    useEffect(() => {
        InicializarCuenta();
        setMontVenta(montosVenta);
        changeStatusCobro(false);
    }, [montosVenta]);

    useEffect(() => {
        if (!calcularCambioEnLetra) {
            setMontoCambioLetra("");
            return;
        }

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(async () => {
            const letra = await MontoEnLetra(montoCambio);
            setMontoCambioLetra(letra);
        }, 300);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [montoCambio, calcularCambioEnLetra]);

    useEffect(() => {
        CalcularCambio();
    }, [montoEfectivo, montoTipoPago])

    return (
        <Modal
            open={openModalCobrarVentas}
            onClose={handleCloseModalCobroVentas}
            aria-labelledby="menu-modal-title"
            aria-describedby="menu-modal-description"
        >
            <Box sx={modalStyle}>
                <Fragment>
                    <div className="containerCobroVentasMenu" >
                        <Tooltip title="Pago en efectivo">
                            <IconButton onClick={() => handleChangeTipoPago('EFECTIVO')} color="primary">
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                    {/* Ícono principal */}
                                    <AttachMoney sx={{ fontSize: 60 }} />

                                    {/* Letra "C" superpuesta */}
                                    <Typography
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0, // Posición inferior
                                            right: 33, // Posición derecha
                                            backgroundColor: 'white', // Fondo blanco para resaltar la letra
                                            borderRadius: '50%', // Forma circular
                                            padding: '2px 6px', // Espaciado para el círculo
                                            fontSize: '14px', // Tamaño de la letra
                                            fontWeight: 'bold',
                                            color: 'black',
                                            transform: 'translate(50%, 50%)' // Desplazamiento para ajustar mejor la posición
                                        }}
                                    >
                                        Alt+E
                                    </Typography>
                                </Box>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Pago con tarjeta crédito">
                            <IconButton onClick={() => handleChangeTipoPago('TARJETA CRÉDITO')} color="primary">
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                    {/* Ícono principal */}
                                    <CreditCard sx={{ fontSize: 60 }} />

                                    {/* Letra "C" superpuesta */}
                                    <Typography
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0, // Posición inferior
                                            right: 33, // Posición derecha
                                            backgroundColor: 'white', // Fondo blanco para resaltar la letra
                                            borderRadius: '50%', // Forma circular
                                            padding: '2px 6px', // Espaciado para el círculo
                                            fontSize: '14px', // Tamaño de la letra
                                            fontWeight: 'bold',
                                            color: 'black',
                                            transform: 'translate(50%, 50%)' // Desplazamiento para ajustar mejor la posición
                                        }}
                                    >
                                        Alt+C
                                    </Typography>
                                </Box>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Pago con tarjeta débito">
                            <IconButton onClick={() => handleChangeTipoPago('TARJETA DÉBITO')} color="primary">
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                    {/* Ícono principal */}
                                    <CreditCard sx={{ fontSize: 60 }} />

                                    {/* Letra "C" superpuesta */}
                                    <Typography
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0, // Posición inferior
                                            right: 33, // Posición derecha
                                            backgroundColor: 'white', // Fondo blanco para resaltar la letra
                                            borderRadius: '50%', // Forma circular
                                            padding: '2px 6px', // Espaciado para el círculo
                                            fontSize: '14px', // Tamaño de la letra
                                            fontWeight: 'bold',
                                            color: 'black',
                                            transform: 'translate(50%, 50%)' // Desplazamiento para ajustar mejor la posición
                                        }}
                                    >
                                        Alt+D
                                    </Typography>
                                </Box>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Pago con cheque">
                            <IconButton onClick={() => handleChangeTipoPago('CHEQUE')} color="primary">
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                    {/* Ícono principal */}
                                    <AccountBalanceWallet sx={{ fontSize: 60 }} />

                                    {/* Letra "C" superpuesta */}
                                    <Typography
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0, // Posición inferior
                                            right: 33, // Posición derecha
                                            backgroundColor: 'white', // Fondo blanco para resaltar la letra
                                            borderRadius: '50%', // Forma circular
                                            padding: '2px 6px', // Espaciado para el círculo
                                            fontSize: '14px', // Tamaño de la letra
                                            fontWeight: 'bold',
                                            color: 'black',
                                            transform: 'translate(50%, 50%)' // Desplazamiento para ajustar mejor la posición
                                        }}
                                    >
                                        Alt+E
                                    </Typography>
                                </Box>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Pago con vales">
                            <IconButton onClick={() => handleChangeTipoPago('VALES')} color="primary">
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                    {/* Ícono principal */}
                                    <CallToAction sx={{ fontSize: 60 }} />

                                    {/* Letra "C" superpuesta */}
                                    <Typography
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0, // Posición inferior
                                            right: 33, // Posición derecha
                                            backgroundColor: 'white', // Fondo blanco para resaltar la letra
                                            borderRadius: '50%', // Forma circular
                                            padding: '2px 6px', // Espaciado para el círculo
                                            fontSize: '14px', // Tamaño de la letra
                                            fontWeight: 'bold',
                                            color: 'black',
                                            transform: 'translate(50%, 50%)' // Desplazamiento para ajustar mejor la posición
                                        }}
                                    >
                                        Alt+V
                                    </Typography>
                                </Box>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Pago mediante transferencia">
                            <IconButton onClick={() => handleChangeTipoPago('TRANSFERENCIA')} color="primary">
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                    {/* Ícono principal */}
                                    <AccountBalance sx={{ fontSize: 60 }} />

                                    {/* Letra "C" superpuesta */}
                                    <Typography
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0, // Posición inferior
                                            right: 33, // Posición derecha
                                            backgroundColor: 'white', // Fondo blanco para resaltar la letra
                                            borderRadius: '50%', // Forma circular
                                            padding: '2px 6px', // Espaciado para el círculo
                                            fontSize: '14px', // Tamaño de la letra
                                            fontWeight: 'bold',
                                            color: 'black',
                                            transform: 'translate(50%, 50%)' // Desplazamiento para ajustar mejor la posición
                                        }}
                                    >
                                        Alt+T
                                    </Typography>
                                </Box>
                            </IconButton>
                        </Tooltip>
                    </div>
                    <div className="venta-detalles-container">
                        <div className="detalle-item">
                            <span className="detalle-text">Descuento:</span> {NumberFormat$(montVenta.totalDescuento)}
                            <span className="detalle-text">Subtotal:</span> {NumberFormat$(montVenta.subTotal)}
                            <span className="detalle-text">IVA:</span> {NumberFormat$(montVenta.totalIVA)}
                            <span className="detalle-text">Total:</span> {NumberFormat$(montVenta.total)}
                        </div>
                        <div><span className="detalle-text">Monto en letra:</span> {montVenta.montoEnLetra}</div>
                    </div>
                    <div className="containerCobroVentas">
                        <div className="actionSectionCobroVenta">
                            <TextField autoComplete="off"
                                name="idCobroTipoPago"
                                label="Tipo de pago:"
                                value={montVenta.tipoPago ? montVenta.tipoPago : ""}
                                onChange={(e) => handleChangeTipoPago(String(e.target.value))}
                                sx={{ width: '250px' }}
                                select
                            >
                                <MenuItem value={1}>--Seleccionar--</MenuItem>
                                {["EFECTIVO", "CHEQUE", "VALES", "TRANSFERENCIA", "TARJETA DÉBITO", "TARJETA CRÉDITO"].map((x) => (
                                    <MenuItem key={x} value={x}>{x}</MenuItem>
                                ))}
                            </TextField>
                            {(montVenta.tipoPago === 'TARJETA DÉBITO' || montVenta.tipoPago === 'TARJETA CRÉDITO') && (
                                <TextField autoComplete="off"
                                    name="DatosTarjeta"
                                    label="4 dígitos tarjeta:"
                                    value={digitosTarjeta}
                                    onChange={(e) => changeDigitosTarjeta(e)}
                                    onFocus={(e) => e.target.select()}
                                    type="text"
                                    inputRef={refDigitos}
                                    inputProps={{ maxLength: 4, minLength: 0 }}
                                    sx={{ margin: '.5rem .3rem', width: '250px' }}
                                />
                            )}
                            {(montVenta.tipoPago === 'CHEQUE') && (
                                <TextField autoComplete="off"
                                    name="DatosCheque"
                                    label="Datos cheque:"
                                    value={datosCheque}
                                    onChange={(e) => changeDatosCheque(e)}
                                    onFocus={(e) => e.target.select()}
                                    type="text"
                                    inputRef={refCheque}
                                    inputProps={{ maxLength: 20, minLength: 0 }}
                                    sx={{ margin: '.5rem .3rem', width: '250px' }}
                                />
                            )}
                            {(montVenta.tipoPago === 'VALES') && (
                                <TextField autoComplete="off"
                                    name="DatosVales"
                                    label="Datos vales:"
                                    value={datosVale}
                                    onChange={(e) => changeDatosVale(e)}
                                    onFocus={(e) => e.target.select()}
                                    type="text"
                                    inputRef={refVales}
                                    inputProps={{ maxLength: 20, minLength: 0 }}
                                    sx={{ margin: '.5rem .3rem', width: '250px' }}
                                />
                            )}
                            {(montVenta.tipoPago === 'TRANSFERENCIA') && (
                                <TextField autoComplete="off"
                                    name="DatosTransferencia"
                                    label="Datos transferencia:"
                                    value={datosTransferencia}
                                    onChange={(e) => changeDatosTransferencia(e)}
                                    onFocus={(e) => e.target.select()}
                                    type="text"
                                    inputRef={refTransferencia}
                                    inputProps={{ maxLength: 20, minLength: 0 }}
                                    sx={{ margin: '.5rem .3rem', width: '250px' }}
                                />
                            )}
                            {typeof onChangeObservacion === "function" && (
                                <TextField autoComplete="off"
                                    label="Observación"
                                    value={observacion}
                                    onChange={(e) => onChangeObservacion(e.target.value)}
                                    multiline
                                    minRows={2}
                                    inputProps={{ maxLength: 500 }}
                                    sx={{ margin: '.5rem .3rem', width: '460px' }}
                                />
                            )}
                        </div>
                        <div className="actionSectionCobroVenta">
                            {montVenta.tipoPago !== "EFECTIVO" && (
                                <TextField
                                    name="MontoTipoPago"
                                    label="Monto tipo pago:"
                                    value={montoTipoPago}
                                    onChange={(e) => changeTipoPago(e)}
                                    onFocus={(e) => e.target.select()}
                                    autoComplete="off"
                                    inputRef={refMontoOtro}
                                    InputProps={{
                                        inputComponent: NumericFormatCustom,
                                    }}
                                    type="text"
                                    sx={{ margin: '.5rem .3rem', width: '250px' }}
                                />
                            )}
                            <TextField
                                name="MontoEfectivo"
                                label="Monto efectivo:"
                                value={montoEfectivo}
                                onChange={(e) => changeEfectivo(e)}
                                onFocus={(e) => e.target.select()}
                                autoComplete="off"
                                inputRef={refMontoEfectivo}
                                InputProps={{
                                    inputComponent: NumericFormatCustom,
                                }}
                                type="text"
                                sx={{ margin: '.5rem .3rem', width: '250px' }}
                            />
                            <hr className="separator" />
                            <TextField autoComplete="off"
                                name="MontoCambio"
                                readOnly
                                label="Monto cambio:"
                                value={
                                    montoCambio
                                        ? `${NumberFormat$(montoCambio)}\n(${montoCambioLetra})`
                                        : `${NumberFormat$(0)}\n(${montoCambioLetra})`
                                }
                                multiline
                                type="text"
                                sx={{
                                    width: '460px',
                                    margin: '.5rem .3rem',
                                    backgroundColor: parseFloat(montoCambio) < 0 ? '#ff3333' : 'white',
                                    color: parseFloat(montoCambio) < 0 ? '#ff3333' : 'black',
                                    fontWeight: 'bold',
                                    '& .MuiInputBase-input': {
                                        backgroundColor: parseFloat(montoCambio) < 0 ? '#ff3333' : 'white',
                                        color: parseFloat(montoCambio) < 0 ? 'white' : 'black',
                                        fontWeight: 'bold',
                                    }
                                }}
                                disabled={true}
                            />

            {(statusCobro === true) && (
                                <>
                                    {bloqueaCobroPorEntregaDefinitiva && (
                                        <Typography
                                            sx={{
                                                width: '460px',
                                                margin: '.5rem .3rem',
                                                padding: '10px',
                                                borderRadius: '6px',
                                                border: '1px solid #d32f2f',
                                                backgroundColor: '#fff4f4',
                                                color: '#b71c1c',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            El monto cubre el 100% del apartado. Cierre este cobro y use el botón Entrega definitiva para imprimir el abono y el acuse de recibo.
                                        </Typography>
                                    )}
                                    <button
                                        className="cobrarVentaButton"
                                        onClick={CobrarVenta}
                                        disabled={isProcessing || bloqueaCobroPorEntregaDefinitiva}
                                    >
                                        Cobrar venta
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </Fragment>
            </Box>
        </Modal>
    );
}
